import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  browseFilesystem,
  type FilesystemNode,
  type FilesystemBrowseError,
} from '@/shared/services/api';
import { Folder, FolderOpen, ChevronRight, HardDrive, X, AlertCircle, Loader2 } from 'lucide-react';

interface FolderBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  initialPath?: string;
  selectedPaths: string[];
  onSelectPaths: (paths: string[]) => void;
  allowMultiple?: boolean;
}

interface DriveInfo {
  letter: string;
  label?: string;
}

export function FolderBrowserModal({
  isOpen,
  onClose,
  agentId,
  initialPath = '',
  selectedPaths,
  onSelectPaths,
  allowMultiple = true,
}: FolderBrowserModalProps) {
  const { t } = useTranslation('backups');
  const [currentPath, setCurrentPath] = useState(initialPath || '');
  const [nodes, setNodes] = useState<FilesystemNode[]>([]);
  const [drives, setDrives] = useState<DriveInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<FilesystemBrowseError | null>(null);
  const [localSelectedPaths, setLocalSelectedPaths] = useState<string[]>(selectedPaths);
  const openerRef = useRef<HTMLElement | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPath(initialPath || '');
      setLocalSelectedPaths(selectedPaths);
      setError('');
      setErrorDetails(null);
    }
  }, [isOpen, initialPath, selectedPaths]);

  useEffect(() => {
    if (isOpen) {
      openerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      requestAnimationFrame(() => document.getElementById('folder-browser-close')?.focus());
    } else {
      openerRef.current?.focus();
      openerRef.current = null;
    }
  }, [isOpen]);

  // Fetch filesystem nodes when path changes
  const fetchNodes = useCallback(
    async (path: string) => {
      if (!agentId) {
        setError(t('folderBrowser.noAgentSelected'));
        return;
      }

      setLoading(true);
      setError('');
      setErrorDetails(null);

      try {
        const response = await browseFilesystem(agentId, path);

        // Filter to show only directories
        const directories = response.nodes.filter((node) => node.type === 'directory');
        setNodes(directories);
        setCurrentPath(response.path);
      } catch (err) {
        const axiosError = err as {
          response?: { data?: FilesystemBrowseError; status?: number };
          message: string;
        };
        const errorData = axiosError.response?.data;

        if (errorData) {
          setErrorDetails(
            errorData.suggestion
              ? { ...errorData, suggestion: t('folderBrowser.connectionSuggestion') }
              : null
          );
          setError(
            errorData.error === 'Agent is offline'
              ? t('folderBrowser.agentOffline')
              : errorData.error === 'Agent request timeout'
                ? t('folderBrowser.agentTimeout')
                : t('folderBrowser.loadError')
          );
        } else {
          setError(t('folderBrowser.loadError'));
        }
        setNodes([]);
      } finally {
        setLoading(false);
      }
    },
    [agentId, t]
  );

  // Initial load - fetch drives if no path specified
  useEffect(() => {
    if (isOpen && agentId) {
      if (!currentPath) {
        // Try to fetch drives by sending empty path
        fetchNodes('');
      } else {
        fetchNodes(currentPath);
      }
    }
  }, [isOpen, agentId, currentPath, fetchNodes]);

  // Parse drives from nodes when at root
  useEffect(() => {
    if (!currentPath && nodes.length > 0) {
      // Check if nodes look like Windows drives (e.g., "C:\")
      const driveNodes = nodes.filter((node) => /^[A-Z]:\\$/i.test(node.name));
      if (driveNodes.length > 0) {
        const driveList: DriveInfo[] = driveNodes.map((node) => ({
          letter: node.name.replace('\\', ''),
          label: node.name,
        }));
        setDrives(driveList);
      } else {
        setDrives([]);
      }
    } else {
      setDrives([]);
    }
  }, [currentPath, nodes]);

  const handleNavigateToPath = (path: string) => {
    fetchNodes(path);
  };

  const handleNavigateUp = () => {
    if (!currentPath) return;

    // Handle Windows paths
    const normalizedPath = currentPath.replace(/\\/g, '/');
    const parts = normalizedPath.split('/').filter(Boolean);

    if (parts.length === 0) {
      setCurrentPath('');
      return;
    }

    parts.pop();

    if (parts.length === 0) {
      setCurrentPath('');
    } else {
      // Check if we're left with just a drive letter (e.g., "C:")
      const parentPath = parts.join('/');
      if (/^[A-Z]:$/i.test(parentPath)) {
        setCurrentPath(parentPath + '/');
      } else {
        setCurrentPath(parentPath + '/');
      }
    }
  };

  const togglePathSelection = (path: string) => {
    if (!allowMultiple) {
      setLocalSelectedPaths([path]);
      return;
    }

    setLocalSelectedPaths((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const removeSelectedPath = (path: string) => {
    setLocalSelectedPaths((prev) => prev.filter((p) => p !== path));
  };

  const handleConfirmSelection = () => {
    onSelectPaths(localSelectedPaths);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedPaths(selectedPaths);
    onClose();
  };

  // Build breadcrumb segments
  const getBreadcrumbSegments = () => {
    if (!currentPath) return [];

    const normalizedPath = currentPath.replace(/\\/g, '/');
    const parts = normalizedPath.split('/').filter(Boolean);

    return parts.map((part, index) => {
      const path = parts.slice(0, index + 1).join('/');
      // Add trailing slash for drive letters
      const fullPath = /^[A-Z]:$/i.test(path) ? path + '/' : path;
      return { name: part, path: fullPath };
    });
  };

  const breadcrumbSegments = getBreadcrumbSegments();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] animate-in fade-in duration-200">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="folder-browser-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setLocalSelectedPaths(selectedPaths);
            onClose();
            return;
          }
          if (event.key === 'Tab') {
            const focusable = Array.from(
              event.currentTarget.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
              )
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        }}
        className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-elevated max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col border border-surface-border-light dark:border-surface-border-dark"
      >
        {/* Header */}
        <div className="p-6 border-b border-surface-border-light dark:border-surface-border-dark flex-shrink-0 bg-surface-2-light dark:bg-surface-2-dark">
          <div className="flex items-center justify-between">
            <div>
              <h2
                id="folder-browser-title"
                className="text-xl font-black text-slate-900 dark:text-white tracking-tight"
              >
                {t('folderBrowser.title')}
              </h2>
              <p className="text-sm text-slate-600 dark:text-text-subtle-dark mt-1 font-medium">
                {t('folderBrowser.subtitle')}
              </p>
            </div>
            <button
              id="folder-browser-close"
              type="button"
              aria-label={t('folderBrowser.close')}
              onClick={handleCancel}
              className="h-11 w-11 shrink-0 inline-flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark hover:text-text-muted-light dark:hover:text-slate-300 rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Drive Selection */}
          {drives.length > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
                {t('folderBrowser.drives')}
              </span>
              {drives.map((drive) => (
                <button
                  type="button"
                  key={drive.letter}
                  onClick={() => handleNavigateToPath(drive.letter + ':/')}
                  className="min-h-11 flex items-center px-3 py-1.5 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-surface-2-light dark:hover:bg-surface-border-dark hover:border-brand-600 dark:hover:border-brand-400 transition-colors"
                >
                  <HardDrive className="h-4 w-4 mr-1.5 text-text-subtle-light dark:text-text-subtle-dark" />
                  {drive.letter}
                </button>
              ))}
            </div>
          )}

          {/* Breadcrumb Navigation */}
          {currentPath && (
            <div className="mt-4 flex items-center space-x-1 flex-wrap text-sm">
              <button
                type="button"
                onClick={() => setCurrentPath('')}
                className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-bold"
              >
                <HardDrive className="h-4 w-4 inline mr-1" />
                {t('folderBrowser.root')}
              </button>
              {breadcrumbSegments.map((segment, idx) => (
                <span key={idx} className="flex items-center">
                  <ChevronRight className="h-4 w-4 text-text-subtle-light dark:text-text-subtle-dark mx-1" />
                  <button
                    type="button"
                    onClick={() => handleNavigateToPath(segment.path)}
                    className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                  >
                    {segment.name}
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Folder List */}
        <div className="flex-1 overflow-y-auto p-6 bg-surface-card-light dark:bg-surface-card-dark">
          {error && (
            <div className="mb-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-state-danger-light dark:text-state-danger-dark shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                    {error}
                  </p>
                  {errorDetails?.suggestion && (
                    <p className="text-sm text-state-danger-light dark:text-state-danger-dark mt-1">
                      {errorDetails.suggestion}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div role="status" aria-live="polite" className="text-center py-12">
              <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-600 dark:text-brand-400" />
              <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
                {t('folderBrowser.loading')}
              </p>
            </div>
          ) : (
            <div className="border border-surface-border-light dark:border-surface-border-dark rounded-xl overflow-hidden bg-surface-2-light dark:bg-surface-2-dark">
              {/* Navigate Up Button */}
              {currentPath && (
                <button
                  type="button"
                  className="w-full min-h-11 sm:min-h-7 flex items-center px-3 py-0 text-left hover:bg-surface-2-light dark:hover:bg-surface-border-dark text-slate-600 dark:text-slate-300 transition-colors border-b border-surface-border-light dark:border-surface-border-dark"
                  onClick={handleNavigateUp}
                >
                  <FolderOpen className="h-5 w-5 text-text-subtle-light dark:text-text-subtle-dark mr-3" />
                  <span className="text-sm font-bold">{t('folderBrowser.parentDirectory')}</span>
                </button>
              )}

              {/* Folders */}
              {nodes.length === 0 ? (
                <div className="text-center py-8 text-text-muted-light dark:text-text-muted-dark font-medium">
                  {currentPath ? t('folderBrowser.emptyDirectory') : t('folderBrowser.selectDrive')}
                </div>
              ) : (
                nodes.map((node, index) => {
                  const isSelected = localSelectedPaths.includes(node.path);
                  return (
                    <div
                      key={node.path}
                      className={`flex min-h-11 sm:min-h-7 items-center gap-2 px-2 sm:px-3 py-0 hover:bg-surface-2-light dark:hover:bg-surface-border-dark transition-colors ${
                        isSelected ? 'bg-state-info-bg-light dark:bg-state-info-bg-dark' : ''
                      } ${index !== nodes.length - 1 ? 'border-b border-surface-border-light dark:border-surface-border-dark' : ''}`}
                    >
                      <label
                        className="min-h-11 sm:min-h-7 min-w-11 sm:min-w-7 shrink-0 inline-flex items-center justify-center cursor-pointer"
                        title={t('folderBrowser.selectFolder', { name: node.name })}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          aria-label={t('folderBrowser.selectFolder', { name: node.name })}
                          onChange={() => togglePathSelection(node.path)}
                          className="h-4 w-4 text-brand-600 dark:text-brand-400 focus:ring-brand-500 border-border-strong-light dark:border-border-strong-dark rounded"
                        />
                      </label>
                      <button
                        type="button"
                        className="flex-1 min-w-0 min-h-11 sm:min-h-7 flex items-center text-left"
                        aria-label={t('folderBrowser.openFolder', { name: node.name })}
                        onClick={() => handleNavigateToPath(node.path)}
                      >
                        <Folder
                          className={`h-5 w-5 mr-3 ${isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-state-warn-light dark:text-state-warn-dark'}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="min-w-0 break-all text-sm font-bold text-slate-900 dark:text-slate-200">
                              {node.name}
                            </span>
                            <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
                              {t('folderBrowser.clickToBrowse')}
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Selected Paths Summary */}
        {localSelectedPaths.length > 0 && (
          <div className="px-6 py-4 border-t border-surface-border-light dark:border-surface-border-dark bg-state-info-bg-light dark:bg-state-info-bg-dark">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {t(
                localSelectedPaths.length === 1
                  ? 'folderBrowser.selectedPathsOne'
                  : 'folderBrowser.selectedPathsOther',
                { count: localSelectedPaths.length }
              )}
            </p>
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              {localSelectedPaths.map((path) => (
                <span
                  key={path}
                  className="inline-flex max-w-full items-center px-2.5 py-1 bg-surface-card-light dark:bg-surface-2-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-300"
                >
                  <Folder className="h-3.5 w-3.5 mr-1.5 text-state-warn-light dark:text-state-warn-dark" />
                  <span className="break-all">{path}</span>
                  <button
                    type="button"
                    aria-label={t('folderBrowser.removeSelectedPath', { path })}
                    onClick={() => removeSelectedPath(path)}
                    className="ml-1.5 h-11 w-11 shrink-0 inline-flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-surface-border-light dark:border-surface-border-dark flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center flex-shrink-0 bg-surface-2-light dark:bg-surface-2-dark">
          <div
            className="text-sm text-text-muted-light dark:text-text-muted-dark"
            aria-live="polite"
          >
            {localSelectedPaths.length > 0 ? (
              <span className="font-medium">
                {t(
                  localSelectedPaths.length === 1
                    ? 'folderBrowser.selectedCountOne'
                    : 'folderBrowser.selectedCountOther',
                  { count: localSelectedPaths.length }
                )}
              </span>
            ) : (
              <span>{t('folderBrowser.noFoldersSelected')}</span>
            )}
          </div>
          <div className="w-full sm:w-auto flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="min-h-11 flex-1 sm:flex-none px-4 py-2 border border-border-strong-light dark:border-border-strong-dark rounded-xl text-slate-700 dark:text-slate-300 hover:bg-surface-2-light dark:hover:bg-surface-border-dark font-semibold text-sm transition-colors"
            >
              {t('folderBrowser.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirmSelection}
              disabled={localSelectedPaths.length === 0}
              className="min-h-11 flex-1 sm:flex-none px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-colors shadow-lg shadow-brand-200 dark:shadow-none"
            >
              {t('folderBrowser.confirm', { count: localSelectedPaths.length })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
